import { c as _c } from "react/compiler-runtime";
import { varAlpha } from 'minimal-shared/utils';
import SvgIcon from '@mui/material/SvgIcon';
import { chipClasses } from '@mui/material/Chip';

// ----------------------------------------------------------------------

/**
 * Icons
 * @see https://icon-sets.iconify.design/solar/close-circle-bold
 */
const ChipDeleteIcon = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path fill="currentColor" fillRule="evenodd" clipRule="evenodd" d="M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0M9 9a1 1 0 0 1 1 0l2 2 2-2a.8.8 0 0 1 1 1l-2 2 2 2a.8.8 0 0 1-1 1l-2-2-2 2a.8.8 0 0 1-1-1l2-2-2-2a1 1 0 0 1 0-1" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <SvgIcon {...props}>{t0}</SvgIcon>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'];
function styleColors(ownerState, styles) {
  const outputStyle = COLORS.reduce((acc, color) => {
    if (!ownerState.disabled && ownerState.color === color) {
      acc = styles(color);
    }
    return acc;
  }, {});
  return outputStyle;
}
const softVariant = {
  colors: COLORS.map(color => ({
    props: ({
      ownerState
    }) => !ownerState.disabled && ownerState.variant === 'soft' && ownerState.color === color,
    style: ({
      theme
    }) => ({
      color: theme.vars.palette[color].dark,
      backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.16),
      '&:hover': {
        backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.32)
      },
      ...theme.applyStyles('dark', {
        color: theme.vars.palette[color].light
      })
    })
  })),
  inheritColor: [{
    props: ({
      ownerState
    }) => ownerState.variant === 'soft' && ownerState.color === 'default',
    style: ({
      theme
    }) => ({
      backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
      '&:hover': {
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.32)
      }
    })
  }]
};
const MuiChip = {
  /** DEFAULT PROPS */
  defaultProps: {
    deleteIcon: <ChipDeleteIcon />
  },
  /** STYLE */
  styleOverrides: {
    root: ({
      ownerState,
      theme
    }) => {
      const styled = {
        colors: styleColors(ownerState, color => ({
          [`& .${chipClasses.avatar}`]: {
            color: theme.vars.palette[color].lighter,
            backgroundColor: theme.vars.palette[color].dark
          }
        })),
        disabled: {
          [`&.${chipClasses.disabled}`]: {
            opacity: 1,
            [`& .${chipClasses.avatar}`]: {
              color: theme.vars.palette.action.disabled,
              backgroundColor: theme.vars.palette.action.disabledBackground
            },
            ...(ownerState.variant === 'outlined' && {
              color: theme.vars.palette.action.disabled,
              borderColor: theme.vars.palette.action.disabledBackground
            }),
            ...(['filled', 'soft'].includes(ownerState.variant) && {
              color: theme.vars.palette.action.disabled,
              backgroundColor: theme.vars.palette.action.disabledBackground
            })
          }
        }
      };
      return {
        variants: [/** @variant soft */
        softVariant.inheritColor, softVariant.colors].flat(),
        ...styled.colors,
        ...styled.disabled
      };
    },
    label: ({
      theme
    }) => ({
      fontWeight: theme.typography.fontWeightMedium
    }),
    icon: {
      color: 'currentColor'
    },
    deleteIcon: {
      opacity: 0.48,
      color: 'currentColor',
      '&:hover': {
        opacity: 1,
        color: 'currentColor'
      }
    },
    /** @sizes */
    sizeMedium: ({
      theme
    }) => ({
      borderRadius: theme.shape.borderRadius * 1.25
    }),
    sizeSmall: ({
      theme
    }) => ({
      borderRadius: theme.shape.borderRadius
    }),
    /** @variant filled */
    filled: ({
      ownerState,
      theme
    }) => {
      const styled = {
        defaultColor: {
          ...(!ownerState.disabled && ownerState.color === 'default' && {
            color: theme.vars.palette.common.white,
            backgroundColor: theme.vars.palette.text.primary,
            [`& .${chipClasses.avatar}`]: {
              color: theme.vars.palette.text.primary
            },
            '&:hover': {
              backgroundColor: theme.vars.palette.grey[700]
            },
            ...theme.applyStyles('dark', {
              color: theme.vars.palette.grey[800],
              '&:hover': {
                backgroundColor: theme.vars.palette.grey[100]
              }
            })
          })
        }
      };
      return {
        ...styled.defaultColor
      };
    },
    /** @variant outlined */
    outlined: ({
      ownerState,
      theme
    }) => {
      const styled = {
        defaultColor: {
          ...(!ownerState.disabled && ownerState.color === 'default' && {
            borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.32)
          })
        }
      };
      return {
        ...styled.defaultColor
      };
    }
  }
};
export const chip = {
  MuiChip
};